import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

const pageStyles = css`
  :host {
    display: block;
    width: 100%;
  }
  .wrap {
    width: 100%;
    max-width: 32rem;
    margin: 0 auto;
    display: grid;
    gap: 1.25rem;
    background: var(--jv-color-surface);
    border: var(--jv-border-w) solid var(--jv-color-border);
    box-shadow: var(--jv-shadow);
    padding: 2rem 1.5rem;
    box-sizing: border-box;
  }
  @media (min-width: 600px) {
    .wrap {
      padding: 2.5rem 2.25rem;
    }
  }
  h1 {
    margin: 0;
    font-size: var(--jv-fs-xl);
    line-height: 1.2;
  }
  p {
    color: var(--jv-color-muted);
    margin: 0;
    line-height: 1.5;
  }
  form {
    display: grid;
    gap: 1rem;
    width: 100%;
  }
  .or {
    text-align: center;
    position: relative;
    font-size: var(--jv-fs-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .err {
    background: #fdf2f2;
    border: 1px solid #f8b4b4;
    color: var(--jv-color-danger);
    padding: 0.75rem 1rem;
    font-size: var(--jv-fs-sm);
    line-height: 1.4;
  }
  .info {
    background: #e3f1ec;
    border: 1px solid #95d5b2;
    color: var(--jv-color-accent-hover);
    padding: 0.75rem 1rem;
    font-size: var(--jv-fs-sm);
    line-height: 1.4;
  }
`;

@customElement('jv-login-page')
export class JvLoginPage extends LitElement {
  static styles = pageStyles;

  @state() private email = '';
  @state() private password = '';
  @state() private error = '';
  @state() private magicNote = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  @state() private loading = false;

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <div class="wrap">
          <h1>${t('auth.loginTitle')}</h1>
          <p>${t('auth.loginBody')}</p>
          <form @submit=${this.onPassword}>
            <jv-form-field
              label=${t('auth.email')}
              name="email"
              type="email"
              autocomplete="email"
              placeholder="name@example.org"
              .value=${this.email}
              required
              @jv-input=${(e: CustomEvent) => (this.email = e.detail.value)}
            ></jv-form-field>
            <jv-form-field
              label=${t('auth.password')}
              name="password"
              type="password"
              autocomplete="current-password"
              placeholder="Your password"
              .value=${this.password}
              required
              @jv-input=${(e: CustomEvent) => (this.password = e.detail.value)}
            ></jv-form-field>
            <jv-button type="submit" block ?disabled=${this.loading}>
              ${this.loading ? 'Signing in…' : t('auth.submit')}
            </jv-button>
          </form>
          <p class="or">${t('auth.or')}</p>
          <jv-button variant="ghost" block @click=${this.onMagic} ?disabled=${this.loading}>${t('auth.magic')}</jv-button>
          ${this.error ? html`<div class="err">${this.error}</div>` : ''}
          ${this.magicNote ? html`<div class="info">${this.magicNote}</div>` : ''}
          <p class="or">
            Don't have an account? <a href="/signup" style="color: var(--jv-color-accent); text-decoration: underline;">Create account</a>
          </p>
        </div>
      </jv-page>
    `;
  }

  private nextPath() {
    const next = new URLSearchParams(location.search).get('next');
    return next || '/portal';
  }

  private goNext() {
    history.pushState({}, '', this.nextPath());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  private onPassword = async (event: Event) => {
    event.preventDefault();
    this.error = '';
    this.loading = true;
    try {
      const session = await store.client.login({ email: this.email.trim(), password: this.password });
      store.setSession(session.user, session.accessToken);
      this.goNext();
    } catch (err: any) {
      console.error('Login error:', err);
      if (err?.details?.error) {
        this.error = err.details.error;
      } else if (err?.message) {
        this.error = err.message;
      } else {
        this.error = 'Invalid email or password';
      }
    } finally {
      this.loading = false;
    }
  };

  private onMagic = async () => {
    try {
      const result = await store.client.magicLink(this.email);
      this.magicNote = result.devToken
        ? `Dev token: ${result.devToken}`
        : 'Sent';
      if (result.devToken) {
        const session = await store.client.verifyMagic(result.devToken);
        store.setSession(session.user, session.accessToken);
        this.goNext();
      }
    } catch {
      this.error = 'Magic link failed';
    }
  };
}

@customElement('jv-signup-page')
export class JvSignupPage extends LitElement {
  static styles = pageStyles;

  @state() private name = '';
  @state() private email = '';
  @state() private password = '';
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  @state() private loading = false;

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <div class="wrap">
          <h1>${t('auth.signupTitle')}</h1>
          <p>${t('auth.signupBody')}</p>
          <form @submit=${this.onSubmit}>
            <jv-form-field
              label=${t('auth.name')}
              name="name"
              placeholder="e.g. Aïssatou Traoré"
              .value=${this.name}
              required
              @jv-input=${(e: CustomEvent) => (this.name = e.detail.value)}
            ></jv-form-field>
            <jv-form-field
              label=${t('auth.email')}
              name="email"
              type="email"
              placeholder="name@example.org"
              .value=${this.email}
              required
              @jv-input=${(e: CustomEvent) => (this.email = e.detail.value)}
            ></jv-form-field>
            <jv-form-field
              label=${t('auth.password')}
              name="password"
              type="password"
              placeholder="Min. 6 characters"
              .value=${this.password}
              required
              @jv-input=${(e: CustomEvent) => (this.password = e.detail.value)}
            ></jv-form-field>
            <jv-button type="submit" block ?disabled=${this.loading}>
              ${this.loading ? 'Creating account…' : t('auth.submit')}
            </jv-button>
          </form>
          ${this.error ? html`<div class="err">${this.error}</div>` : ''}
          <p class="or">
            Already have an account? <a href="/login" style="color: var(--jv-color-accent); text-decoration: underline;">Log in</a>
          </p>
        </div>
      </jv-page>
    `;
  }

  private onSubmit = async (event: Event) => {
    event.preventDefault();
    this.error = '';

    if (!this.name || this.name.trim().length < 2) {
      this.error = 'Name must be at least 2 characters';
      return;
    }
    if (!this.email || !this.email.includes('@')) {
      this.error = 'Please enter a valid email address';
      return;
    }
    if (!this.password || this.password.length < 6) {
      this.error = 'Password must be at least 6 characters';
      return;
    }

    this.loading = true;
    try {
      const session = await store.client.register({
        name: this.name.trim(),
        email: this.email.trim(),
        password: this.password,
        language: store.locale,
      });
      store.setSession(session.user, session.accessToken);
      history.pushState({}, '', '/portal');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err: any) {
      console.error('Registration failed:', err);
      if (err?.details?.error) {
        this.error = err.details.error;
      } else if (err?.details?.message) {
        this.error = err.details.message;
      } else if (err?.message) {
        this.error = err.message;
      } else {
        this.error = 'Could not register account. Please check your network connection.';
      }
    } finally {
      this.loading = false;
    }
  };
}
